/**
 * Unified Artifact Details for Context Panel
 * 
 * Displays and edits artifact details for both Prime and Company artifacts.
 * Detects artifact type and uses appropriate API endpoints.
 */

import React, { useState } from 'react';
import { EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { EnhancedArtifact } from '../../../types/artifacts';
import { PrimeArtifactService } from '../../../services/primeArtifactService';
import api from '../../../services/api/api';

interface UnifiedArtifactDetailsProps {
  artifact: EnhancedArtifact;
  isPrime?: boolean; // If not specified, will auto-detect from artifact properties
  onUpdate?: (updated: EnhancedArtifact) => void;
}

export const UnifiedArtifactDetails: React.FC<UnifiedArtifactDetailsProps> = ({
  artifact,
  isPrime,
  onUpdate
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(artifact.name);
  const [editedDescription, setEditedDescription] = useState(artifact.description || '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-detect if prime artifact
  const isPrimeArtifact = isPrime ?? (
    // Prime artifacts have prime_artifact_id pointing to themselves or no artifact_tree_id
    !artifact.artifact_tree_id || artifact.prime_artifact_id === artifact.id
  );

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    
    try {
      const updates = {
        name: editedName,
        description: editedDescription
      };

      if (isPrimeArtifact) {
        // Update prime artifact using the update method with partial data
        await PrimeArtifactService.update(artifact.id, updates);
      } else {
        // Update company artifact
        await api.patch(`/artifacts/${artifact.id}`, updates);
      }

      // Create updated artifact
      const updatedArtifact = {
        ...artifact,
        ...updates
      };

      onUpdate?.(updatedArtifact);
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to update artifact:', err);
      setError(err instanceof Error ? err.message : 'Failed to update artifact');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedName(artifact.name);
    setEditedDescription(artifact.description || '');
    setError(null);
    setIsEditing(false);
  };

  return (
    <div style={{ fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {/* Edit/Save Buttons */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            style={{
              padding: '4px 12px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '11px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <EditOutlined style={{ fontSize: '12px' }} />
            Edit
          </button>
        ) : (
          <>
            <button
              onClick={handleSave}
              disabled={isSaving}
              style={{
                padding: '4px 12px',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '11px',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                opacity: isSaving ? 0.7 : 1
              }}
            >
              <SaveOutlined style={{ fontSize: '12px' }} />
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={handleCancel}
              disabled={isSaving}
              style={{
                padding: '4px 12px',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '11px',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <CloseOutlined style={{ fontSize: '12px' }} />
              Cancel
            </button>
          </>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          padding: '8px',
          backgroundColor: '#fee2e2',
          border: '1px solid #ef4444',
          borderRadius: '4px',
          color: '#991b1b',
          fontSize: '11px'
        }}>
          {error}
        </div>
      )}

      {/* Name Field */}
      <div>
        <div style={{ fontWeight: '500', fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
          Name {isEditing && <span style={{ color: '#ef4444' }}>*</span>}
        </div>
        {isEditing ? (
          <input
            type="text"
            value={editedName}
            onChange={(e) => setEditedName(e.target.value)}
            style={{
              width: '100%',
              padding: '6px 8px',
              fontSize: '12px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              outline: 'none'
            }}
            disabled={isSaving}
          />
        ) : (
          <div style={{ color: '#374151', fontWeight: '500' }}>{artifact.name}</div>
        )}
      </div>

      {/* Description Field */}
      <div>
        <div style={{ fontWeight: '500', fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
          Description
        </div>
        {isEditing ? (
          <textarea
            value={editedDescription}
            onChange={(e) => setEditedDescription(e.target.value)}
            rows={3}
            style={{
              width: '100%',
              padding: '6px 8px',
              fontSize: '12px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              outline: 'none',
              resize: 'vertical',
              fontFamily: 'inherit'
            }}
            disabled={isSaving}
          />
        ) : (
          <div style={{ color: '#374151' }}>
            {artifact.description || <em style={{ color: '#9ca3af' }}>No description</em>}
          </div>
        )}
      </div>

      {/* Type */}
      <div>
        <div style={{ fontWeight: '500', fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
          Type
        </div>
        <div style={{ color: '#374151' }}>{artifact.type}</div>
      </div>

      {/* Status */}
      {artifact.status && (
        <div>
          <div style={{ fontWeight: '500', fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
            Status
          </div>
          <div style={{ color: '#374151' }}>{artifact.status}</div>
        </div>
      )}

      {/* Artifact Source */}
      <div>
        <div style={{ fontWeight: '500', fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
          Source
        </div>
        <div style={{ 
          color: '#374151',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          {isPrimeArtifact ? (
            <>
              <span style={{
                padding: '2px 6px',
                backgroundColor: '#dbeafe',
                color: '#1e40af',
                borderRadius: '3px',
                fontSize: '10px',
                fontWeight: '600'
              }}>
                PRIME
              </span>
              Prime Model Artifact
            </>
          ) : (
            <>
              <span style={{
                padding: '2px 6px',
                backgroundColor: '#dcfce7',
                color: '#166534',
                borderRadius: '3px',
                fontSize: '10px',
                fontWeight: '600'
              }}>
                COMPANY
              </span>
              Company Instance Artifact
            </>
          )}
        </div>
      </div>

      {/* ID */}
      <div>
        <div style={{ fontWeight: '500', fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
          ID
        </div>
        <div style={{ 
          color: '#6b7280', 
          fontSize: '10px',
          fontFamily: 'monospace',
          wordBreak: 'break-all',
          backgroundColor: '#f9fafb',
          padding: '4px 6px',
          borderRadius: '3px'
        }}>
          {artifact.id}
        </div>
      </div>

      {/* Parent Reference (if exists) */}
      {artifact.parent_ref && (
        <div>
          <div style={{ fontWeight: '500', fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
            Parent
          </div>
          <div style={{ color: '#374151' }}>
            {artifact.parent?.name || artifact.parent_ref}
          </div>
        </div>
      )}

      {/* Created/Updated */}
      {(artifact.created_at || artifact.updated_at) && (
        <div style={{
          paddingTop: '8px',
          borderTop: '1px solid #e5e7eb',
          fontSize: '10px',
          color: '#9ca3af'
        }}>
          {artifact.created_at && (
            <div>Created: {new Date(artifact.created_at).toLocaleString()}</div>
          )}
          {artifact.updated_at && (
            <div>Updated: {new Date(artifact.updated_at).toLocaleString()}</div>
          )}
        </div>
      )}
    </div>
  );
};

